import React, { useState, useContext } from "react";
import { UserContext } from "../../../contexts/user.context";
import { createProfile } from "../../../services/profile.service";
import { 
  AGE_BRACKETS, GENDERS, RELIGIONS, NATIONALITIES, 
  WORK_ARRANGEMENTS, SMOKING_HABITS, PARTY_HABITS, 
  COOKING_HABITS, PET_OPTIONS, GENDER_PREFERENCES, 
  HABIT_OPTIONS, DISLIKE_OPTIONS 
} from "../../../utils/constants";

export default function ProfileRegister({ onCompleted }) {
  const { user, fsUser, setUserProfile } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [customHabit, setCustomHabit] = useState("");
  const [customDislike, setCustomDislike] = useState("");

  const [formData, setFormData] = useState({
    ageBracket: "",
    gender: "",
    religion: "",
    nationality: "",
    workArrangement: "",
    smoking: "",
    houseParties: "",
    cooking: "",
    pets: "",
    currentOccupants: "",
    ruumieGenderPreference: "",
    habits: [],
    dislikes: []
  });

  const handleToggleTag = (type, value) => {
    setFormData((prev) => {
      const currentTags = prev[type];
      const isSelected = currentTags.includes(value);
      return {
        ...prev,
        [type]: isSelected 
          ? currentTags.filter((tag) => tag !== value)
          : [...currentTags, value]
      };
    });
  };

  const handleAddCustomTag = (type, value, setInputState) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    setFormData((prev) => {
      const currentTags = prev[type];
      if (currentTags.includes(trimmedValue)) return prev;
      return {
        ...prev,
        [type]: [...currentTags, trimmedValue]
      };
    });
    setInputState("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.ageBracket || !formData.gender || !formData.ruumieGenderPreference) {
      return setError("Please fill out all required fields.");
    }

    setLoading(true);

    try {
      if (!fsUser?.uid) throw new Error("Authentication error. Please log in again.");
      
      const payload = {
        ...formData,
        username: user?.username || user?.display_name || user?.user_email?.split('@')[0] || "Ruumie",
      };

      await createProfile(fsUser.uid, payload);
      
      setUserProfile({ ...payload, uid: fsUser.uid, isComplete: true });
      onCompleted();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col animate-fade-in mt-6">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-8">
        <h4 className="text-blue-800 font-semibold mb-1 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Why do we need this?
        </h4>
        <p className="text-sm text-blue-700">
          To find your perfect match! Be honest about your habits and preferences so we can pair you with the best possible Ruumies. This information will be visible on your public profile.
        </p>
      </div>

      <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Your Ruumie Profile</h3>

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* SECTION 1: Social Profile */}
        <div className="space-y-5">
          <h4 className="text-lg font-bold text-gray-800 border-b pb-2">Social Profile</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ageBracket" className="block text-sm font-medium text-gray-700 mb-1">Age Bracket *</label>
              <select
                id="ageBracket"
                required
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.ageBracket}
                onChange={(e) => setFormData({ ...formData, ageBracket: e.target.value })}
              >
                <option value="">Select Age</option>
                {AGE_BRACKETS.map(age => <option key={age} value={age}>{age}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Your Gender *</label>
              <select
                id="gender"
                required
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <option value="">Select Gender</option>
                {GENDERS.map(gender => <option key={gender} value={gender}>{gender}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="religion" className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
              <select
                id="religion"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white text-sm"
                value={formData.religion}
                onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
              >
                <option value="">Select Religion</option>
                {RELIGIONS.map(rel => <option key={rel} value={rel}>{rel}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
              <select
                id="nationality"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white text-sm"
                value={formData.nationality}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
              >
                <option value="">Select Country</option>
                {NATIONALITIES.map(nat => <option key={nat} value={nat}>{nat}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="workArrangement" className="block text-sm font-medium text-gray-700 mb-1">Work Setup</label>
              <select
                id="workArrangement"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.workArrangement}
                onChange={(e) => setFormData({ ...formData, workArrangement: e.target.value })}
              >
                <option value="">Select Setup</option>
                {WORK_ARRANGEMENTS.map(work => (
                  <option key={work.value} value={work.value}>{work.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* SECTION 2: Habits & Lifestyle */}
        <div className="space-y-5">
          <h4 className="text-lg font-bold text-gray-800 border-b pb-2">Habits & Lifestyle</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="smoking" className="block text-sm font-medium text-gray-700 mb-1">Smoking Habit</label>
              <select
                id="smoking"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.smoking}
                onChange={(e) => setFormData({ ...formData, smoking: e.target.value })}
              >
                <option value="">Select Option</option>
                {SMOKING_HABITS.map(habit => (
                  <option key={habit.value} value={habit.value}>{habit.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="houseParties" className="block text-sm font-medium text-gray-700 mb-1">House Parties</label>
              <select
                id="houseParties"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.houseParties}
                onChange={(e) => setFormData({ ...formData, houseParties: e.target.value })}
              >
                <option value="">Select Option</option>
                {PARTY_HABITS.map(party => (
                  <option key={party.value} value={party.value}>{party.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cooking" className="block text-sm font-medium text-gray-700 mb-1">Cooking Frequency</label>
              <select
                id="cooking"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.cooking}
                onChange={(e) => setFormData({ ...formData, cooking: e.target.value })}
              >
                <option value="">Select Option</option>
                {COOKING_HABITS.map(cook => (
                  <option key={cook.value} value={cook.value}>{cook.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pets" className="block text-sm font-medium text-gray-700 mb-1">Do you have Pets?</label>
              <select
                id="pets"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.pets}
                onChange={(e) => setFormData({ ...formData, pets: e.target.value })}
              >
                <option value="">Select Pet</option>
                {PET_OPTIONS.map(pet => (
                  <option key={pet.value} value={pet.value}>{pet.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="block text-sm font-medium text-gray-700 mb-2">Other Habits (Select all that apply)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {HABIT_OPTIONS.map(habit => (
                <button
                  key={habit}
                  type="button"
                  onClick={() => handleToggleTag('habits', habit)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors border ${
                    formData.habits.includes(habit)
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-600 border-gray-300 hover:border-primary"
                  }`}
                >
                  {habit}
                </button>
              ))}
              {formData.habits.filter(h => !HABIT_OPTIONS.includes(h)).map(custom => (
                <button
                  key={custom}
                  type="button"
                  onClick={() => handleToggleTag('habits', custom)}
                  className="px-3 py-1.5 text-sm rounded-full transition-colors border bg-primary text-white border-primary flex items-center gap-1"
                >
                  {custom} 
                  <span className="text-white/80 hover:text-white text-lg leading-none">&times;</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add custom habit..."
                value={customHabit}
                onChange={(e) => setCustomHabit(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomTag('habits', customHabit, setCustomHabit);
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-md outline-none focus:border-primary text-sm"
              />
              <button
                type="button"
                onClick={() => handleAddCustomTag('habits', customHabit, setCustomHabit)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <div className="block text-sm font-medium text-gray-700 mb-2">My Dislikes (Select all that apply)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {DISLIKE_OPTIONS.map(dislike => (
                <button
                  key={dislike}
                  type="button"
                  onClick={() => handleToggleTag('dislikes', dislike)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors border ${
                    formData.dislikes.includes(dislike)
                      ? "bg-red-500 text-white border-red-500"
                      : "bg-white text-gray-600 border-gray-300 hover:border-red-400"
                  }`}
                >
                  {dislike}
                </button>
              ))}
              {formData.dislikes.filter(d => !DISLIKE_OPTIONS.includes(d)).map(custom => (
                <button
                  key={custom}
                  type="button"
                  onClick={() => handleToggleTag('dislikes', custom)}
                  className="px-3 py-1.5 text-sm rounded-full transition-colors border bg-red-500 text-white border-red-500 flex items-center gap-1"
                >
                  {custom}
                  <span className="text-white/80 hover:text-white text-lg leading-none">&times;</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add custom dislike..."
                value={customDislike}
                onChange={(e) => setCustomDislike(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomTag('dislikes', customDislike, setCustomDislike);
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-md outline-none focus:border-primary text-sm"
              />
              <button
                type="button"
                onClick={() => handleAddCustomTag('dislikes', customDislike, setCustomDislike)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* SECTION 3: Living Situation & Preferences */}
        <div className="space-y-5">
          <h4 className="text-lg font-bold text-gray-800 border-b pb-2">Living Situation & Preferences</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="currentOccupants" className="block text-sm font-medium text-gray-700 mb-1">No. of Current Occupants</label>
              <input
                id="currentOccupants"
                type="number"
                min="0"
                placeholder="e.g. 2"
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.currentOccupants}
                onChange={(e) => setFormData({ ...formData, currentOccupants: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="ruumieGenderPreference" className="block text-sm font-medium text-gray-700 mb-1">Ruumie Gender Preference *</label>
              <select
                id="ruumieGenderPreference"
                required
                className="w-full p-3 border border-gray-300 rounded-md outline-none focus:border-primary bg-white"
                value={formData.ruumieGenderPreference}
                onChange={(e) => setFormData({ ...formData, ruumieGenderPreference: e.target.value })}
              >
                <option value="">Select Preference</option>
                {GENDER_PREFERENCES.map(pref => (
                  <option key={pref.value} value={pref.value}>{pref.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-white py-4 px-4 rounded-md hover:bg-opacity-90 transition font-bold disabled:opacity-50 mt-8 shadow-sm"
        >
          {loading ? "Saving Profile..." : "Save & Continue"}
        </button>
      </form>
    </div>
  );
}